import { useCallback, useEffect, useRef, type PointerEvent } from 'react';
import { NUDGE_STEP } from './slideGeometry';

/** Lucide chevrons, from the vendored icon font the shell uses. */
const GLYPH = {
  up: String.fromCodePoint(0xe070),
  left: String.fromCodePoint(0xe06e),
  right: String.fromCodePoint(0xe06f),
  down: String.fromCodePoint(0xe06d),
} as const;

type Direction = keyof typeof GLYPH;

const MOVES: Readonly<Record<Direction, { dx: number; dy: number; label: string }>> = {
  up: { dx: 0, dy: -NUDGE_STEP, label: 'Move up 1%' },
  left: { dx: -NUDGE_STEP, dy: 0, label: 'Move left 1%' },
  right: { dx: NUDGE_STEP, dy: 0, label: 'Move right 1%' },
  down: { dx: 0, dy: NUDGE_STEP, label: 'Move down 1%' },
};

/** A held button waits this long, then repeats at the interval below. */
const REPEAT_DELAY_MS = 400;
const REPEAT_EVERY_MS = 80;

interface NudgePadProps {
  /** The selected layer in words ("Caption"); null when none is selected. */
  target: string | null;
  disabled?: boolean;
  /** The arrow keys' own move, applied to the selected layer. */
  onNudge: (dx: number, dy: number) => void;
}

/**
 * Four buttons that move the selected canvas layer 1% a press, for a screen
 * where a fingertip covers the thing it is placing (mobile plan, ruling 14).
 * They call the same move the arrow keys make, so a nudge here and a key press
 * on the layer commit exactly the same position.
 *
 * Holding a button repeats after a short wait and stops when the pointer lifts,
 * leaves or is cancelled. A keyboard press is one step (the platform repeats
 * the click while Enter is held). Shown at 900px and below; the CSS hides it
 * wider, where a mouse can drag precisely.
 */
export function NudgePad({ target, disabled = false, onNudge }: NudgePadProps) {
  const isOff = disabled || target === null;
  // The callback a held button repeats is read fresh on every tick: each step
  // re-renders the editor with the layer's new position.
  const nudgeRef = useRef(onNudge);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    nudgeRef.current = onNudge;
  }, [onNudge]);

  const stop = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => stop, [stop]);

  useEffect(() => {
    if (isOff) stop();
  }, [isOff, stop]);

  function start(direction: Direction, event: PointerEvent<HTMLButtonElement>) {
    if (isOff || event.button !== 0) return;
    const { dx, dy } = MOVES[direction];
    stop();
    nudgeRef.current(dx, dy);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = window.setInterval(() => nudgeRef.current(dx, dy), REPEAT_EVERY_MS);
    }, REPEAT_DELAY_MS);
  }

  function button(direction: Direction) {
    const { dx, dy, label } = MOVES[direction];
    return (
      <button
        className={`nudge-pad__button nudge-pad__button--${direction}`}
        type="button"
        aria-label={label}
        disabled={isOff}
        onPointerDown={(event) => start(direction, event)}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        onContextMenu={(event) => event.preventDefault()}
        onClick={(event) => {
          // A pointer press already moved on pointerdown; detail 0 is a keyboard click.
          if (event.detail === 0 && !isOff) onNudge(dx, dy);
        }}
      >
        <span aria-hidden="true">{GLYPH[direction]}</span>
      </button>
    );
  }

  return (
    <div className="nudge-pad" role="group" aria-label="Nudge the selected layer">
      <div className="nudge-pad__grid">
        {button('up')}
        {button('left')}
        {button('right')}
        {button('down')}
      </div>
      <p className="nudge-pad__note" aria-live="polite">
        {target === null ? 'Select the caption or the logo on the slide to move it.' : `Moving: ${target}`}
      </p>
    </div>
  );
}
